import { useEffect, useState } from "react";
import { ArrowRight, Clock, RotateCcw, X } from "lucide-react";
import { getLastBooking, clearLastBooking, type LastBooking } from "@/lib/lastBooking";
import { track } from "@/lib/analytics";

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 1) return "idag";
  if (days === 1) return "igår";
  if (days < 14) return `för ${days} dagar sedan`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `för ${weeks} veckor sedan`;
  const months = Math.floor(days / 30);
  return `för ${months} mån sedan`;
}

export function BookAgainBlock() {
  const [entry, setEntry] = useState<LastBooking | null>(null);

  useEffect(() => {
    const e = getLastBooking();
    setEntry(e);
    if (e) {
      track("book_again_impression", {
        salon_name: e.salonName,
        barber_name: e.barberName,
        service_name: e.serviceName,
        cta_location: "book_again",
      });
    }
  }, []);

  if (!entry) return null;

  const title = entry.barberName
    ? `Boka igen hos ${entry.barberName}`
    : `Boka igen på ${entry.salonName}`;
  const sub = [entry.serviceName, entry.salonName].filter(Boolean).join(" · ");

  return (
    <section className="container-page pt-6" aria-labelledby="book-again-heading">
      <div className="card-elev p-4 sm:p-5 flex items-center gap-4 bg-brass/10 border-brass/30">
        <div className="hidden sm:flex h-12 w-12 shrink-0 rounded-full bg-brass/20 items-center justify-center text-brass-deep">
          <RotateCcw size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-brass-deep flex items-center gap-1.5">
            <RotateCcw size={12} className="sm:hidden" />
            Senast bokat
          </div>
          <h2 id="book-again-heading" className="mt-1 text-base sm:text-lg font-semibold truncate">
            {title}
          </h2>
          <div className="mt-0.5 text-xs sm:text-sm text-muted-foreground flex items-center gap-2 truncate">
            <span className="truncate">{sub}</span>
            <span className="hidden sm:inline-flex items-center gap-1 text-muted-foreground/70">
              <Clock size={11} /> {timeAgo(entry.timestamp)}
            </span>
          </div>
        </div>
        <a
          href={entry.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            const params = {
              cta_location: "book_again",
              salon_name: entry.salonName,
              barber_name: entry.barberName,
              service_name: entry.serviceName,
            };
            track("book_again_click", params);
            track("book_now_click", params);
          }}
          className="btn-brass shrink-0 inline-flex items-center gap-1.5"
        >
          Boka igen <ArrowRight size={16} />
        </a>
        <button
          type="button"
          aria-label="Rensa senast bokat"
          title="Rensa senast bokat"
          onClick={() => {
            track("book_again_dismiss", {
              cta_location: "book_again",
              salon_name: entry.salonName,
              barber_name: entry.barberName,
              service_name: entry.serviceName,
            });
            clearLastBooking();
            setEntry(null);
          }}
          className="p-1.5 -mr-1 text-muted-foreground hover:text-foreground shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </section>
  );
}
